import { NavLink } from "react-router-dom";
import { ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { Locale, MessageKey } from "../../i18n/dictionaries";
import { Icon, type IconName } from "../../ui/icons";
import { usePreferences } from "../account/use-preferences";
import {
  useMarkNotificationsRead,
  useNotifications,
  type NotificationItem,
  type NotificationRefKind,
} from "./use-notifications";

// BİLDİRİMLER — the staff notification surface (secondary-surfaces §2), replacing the
// HESAP-cluster placeholder (#201) with a real read over the READY feed (#236). A
// calm, scannable, chronological list drawn on OUR design system (`surface`/`k-`/
// `inbox-*` kit + existing tokens) — NO new palette, NO restyled buttons. Grouped
// by severity (Kritik → Bildirimler), server-ordered within each group (critical
// first, newest first — never re-sorted here). Each item is an icon + i18n type
// label + relative time + a DEEP LINK into the item's own surface. Unread items are
// subtly distinct (a review-blue rail + weight); clicking a row (or "Okundu işaretle")
// marks it read via POST → refetch. Critical items (PAID_NOT_ISSUED, SLA breach) carry
// the genuine-alarm RED badge — ONLY those; everything else stays calm. Honest-empty
// ("Bildirim yok — her şey güncel"), loading, error. NO marketing content.

// ref.kind → the EXISTING console route. Detail-routed kinds (issuance/:id,
// recommendations/:id, households/:id) deep-link to the item; the list-only kinds
// (approvals inbox, queue, renewals) deep-link to their surface — the operator lands
// exactly where the signal is actionable.
function refHref(kind: NotificationRefKind, id: string): string {
  switch (kind) {
    case "approval":
      return "/approvals";
    case "queue_item":
      return "/queue";
    case "policy_application":
      return `/issuance/${id}`;
    case "recommendation":
      return `/recommendations/${id}`;
    case "household":
      return `/households/${id}`;
    case "renewal_schedule":
      return "/renewals";
  }
}

// type (+ severity for SLA) → the outline glyph. Calm, neutral tone; colour never
// carries severity here — the red badge does, on critical only.
function itemIcon(item: NotificationItem): IconName {
  switch (item.type) {
    case "APPROVAL":
      return "check-circle";
    case "SLA":
      return "alert-triangle";
    case "PAID_NOT_ISSUED":
      return "alert-circle";
    case "CUSTOMER_RESPONSE":
      return "conversation";
    case "HOUSEHOLD_ASSIGNED":
      return "household";
    case "RENEWAL_FORCED_HIGH":
      return "calendar";
  }
}

// type (+ severity for SLA breach vs risk) → an i18n label. Rendered instead of the
// server's TR-only `title`, so the feed reads cleanly in both locales.
function itemLabelKey(item: NotificationItem): MessageKey {
  switch (item.type) {
    case "APPROVAL":
      return "notifications.type.APPROVAL";
    case "SLA":
      return item.severity === "critical"
        ? "notifications.type.SLA_BREACH"
        : "notifications.type.SLA_RISK";
    case "PAID_NOT_ISSUED":
      return "notifications.type.PAID_NOT_ISSUED";
    case "CUSTOMER_RESPONSE":
      return "notifications.type.CUSTOMER_RESPONSE";
    case "HOUSEHOLD_ASSIGNED":
      return "notifications.type.HOUSEHOLD_ASSIGNED";
    case "RENEWAL_FORCED_HIGH":
      return "notifications.type.RENEWAL_FORCED_HIGH";
  }
}

// Locale-aware relative time (Intl, no dependency) — "2 hours ago" / "2 saat önce".
function relativeTime(iso: string, locale: Locale): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.round((then - Date.now()) / 1000); // negative = past
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
}

export function NotificationsPage() {
  const { t } = useT();
  const { data, isLoading, error, refetch, isFetching } = useNotifications();
  const markRead = useMarkNotificationsRead();

  // GAP-AUDIT-3 D9 — the "Sadece kritik bildirimler" switch in Hesap & ayarlar was
  // written through PUT /v1/me/preferences and consumed by NOTHING: the feed kept
  // showing every normal-severity item. The advisor had been told the noise was
  // turned down and it was not. `severity` is already on the wire, so the floor is
  // honoured here at render, and the hidden count is STATED rather than silently
  // dropped — a filter that hides work without saying so is its own hazard.
  const prefs = usePreferences();
  const criticalOnly = prefs.data?.notificationPref?.severityThreshold === "critical_only";

  const all = data?.data ?? [];
  const items = criticalOnly ? all.filter((i) => i.severity === "critical") : all;
  const hiddenCount = all.length - items.length;
  const unreadKeys = items.filter((i) => !i.read).map((i) => i.key);

  // Partition by severity, preserving the server order within each group (the feed is
  // already sorted critical-first then newest-first). Two calm buckets — no more.
  const critical = items.filter((i) => i.severity === "critical");
  const normal = items.filter((i) => i.severity === "normal");

  return (
    <section className="surface" data-testid={data ? "notifications" : undefined}>
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("notifications.heading")}</h1>
          <p className="surface__subtitle">{t("notifications.subtitle")}</p>
        </div>
        {unreadKeys.length > 0 && (
          <button
            type="button"
            className="k-btn k-btn--soft k-btn--sm"
            disabled={markRead.isPending}
            onClick={() => markRead.mutate({ keys: unreadKeys })}
            data-testid="notif-mark-all"
          >
            {t("notifications.markAll")}
          </button>
        )}
      </header>

      {isLoading && (
        <div className="k-loading" data-testid="notifications-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("notifications.loading")}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="k-note k-note--error" data-testid="notifications-error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("notifications.error.title")}</p>
              <p role="alert" className="k-note__desc">
                {error instanceof ProblemError
                  ? error.problem.detail
                  : t("notifications.error.detail")}
              </p>
              <p className="k-note__desc gunum-reassure">{t("notifications.error.reassure")}</p>
              <div className="k-note__actions">
                <button
                  type="button"
                  className="k-btn k-btn--soft k-btn--sm"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                  data-testid="notifications-retry"
                >
                  {t("notifications.retry")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The empty state must NOT claim "all clear" while the filter is hiding work —
          that is precisely the hazard D9 set out to close. When everything visible was
          filtered away, only the hidden-count line below renders. */}
      {data && items.length === 0 && hiddenCount === 0 && (
        <div className="k-empty" data-testid="notifications-empty">
          <div className="k-empty__icon">
            <Icon name="check-circle" size={26} />
          </div>
          <p className="k-empty__title">{t("notifications.empty.title")}</p>
          <p className="k-empty__desc">{t("notifications.empty.desc")}</p>
        </div>
      )}

      {/* A filter that hides work without saying so is its own hazard — state it. */}
      {data && criticalOnly && hiddenCount > 0 && (
        <p className="k-field__hint" data-testid="notif-filtered">
          {`${hiddenCount} ${t("notifications.filtered")}`}
        </p>
      )}

      {data && items.length > 0 && (
        <div className="notif-groups">
          {critical.length > 0 && (
            <NotificationGroup
              testId="notif-group-critical"
              titleKey="notifications.group.critical"
              icon="alert-triangle"
              items={critical}
              onRead={(k, read) => markRead.mutate({ keys: [k], read })}
            />
          )}
          {normal.length > 0 && (
            <NotificationGroup
              testId="notif-group-normal"
              titleKey="notifications.group.normal"
              icon="alert"
              items={normal}
              onRead={(k, read) => markRead.mutate({ keys: [k], read })}
            />
          )}
        </div>
      )}
    </section>
  );
}

function NotificationGroup({
  testId,
  titleKey,
  icon,
  items,
  onRead,
}: {
  testId: string;
  titleKey: MessageKey;
  icon: IconName;
  items: NotificationItem[];
  onRead: (key: string, read: boolean) => void;
}) {
  const { t } = useT();
  return (
    <div className="k-card gunum-widget" data-testid={testId}>
      <div className="gunum-widget__head">
        <span className="k-stat__icon">
          <Icon name={icon} size={18} />
        </span>
        <span className="gunum-widget__title">{t(titleKey)}</span>
      </div>
      <ul className="inbox-list">
        {items.map((item) => (
          <NotificationRow key={item.key} item={item} onRead={onRead} />
        ))}
      </ul>
    </div>
  );
}

function NotificationRow({
  item,
  onRead,
}: {
  item: NotificationItem;
  onRead: (key: string, read: boolean) => void;
}) {
  const { t, locale } = useT();
  const href = refHref(item.ref.kind, item.ref.id);
  const isCritical = item.severity === "critical";

  return (
    <li
      className="k-card k-card--pad-sm inbox-row notif-row"
      data-testid={`notif-row-${item.key}`}
      data-severity={item.severity}
      {...(item.read ? {} : { "data-unread": "true" })}
    >
      {/* Clicking the row deep-links to the item AND marks it read (if unread). */}
      <NavLink
        to={href}
        className="notif-row__main"
        data-testid={`notif-link-${item.key}`}
        onClick={() => {
          if (!item.read) onRead(item.key, true);
        }}
      >
        <span className="k-stat__icon" aria-hidden="true">
          <Icon name={itemIcon(item)} size={18} />
        </span>
        <span className="notif-row__body">
          <span className="notif-row__title">
            {!item.read && (
              <span className="notif-row__dot" aria-label={t("notifications.unread")} />
            )}
            {t(itemLabelKey(item))}
          </span>
          {/* GAP-AUDIT-3 D15 — `title` is the server's operational label (which
              household, which policy) and it was thrown away in favour of the
              generic type label alone. Twelve SLA breaches therefore rendered as
              twelve identical rows and the advisor could not tell them apart
              without opening each one. The type label stays primary (it reads
              cleanly in both locales); the title rides beneath it as the
              distinguishing detail. */}
          {item.title && (
            <span className="notif-row__detail" data-testid={`notif-title-${item.key}`}>
              {item.title}
            </span>
          )}
          <span className="notif-row__time">{relativeTime(item.occurredAt, locale)}</span>
        </span>
      </NavLink>

      <span className="inbox-actions">
        {/* Severity: the genuine-alarm RED badge on critical only — no badge spam. */}
        {isCritical && (
          <span className="k-badge k-badge--priority" data-testid={`notif-critical-${item.key}`}>
            {t("notifications.critical")}
          </span>
        )}
        {/* D15 — read is no longer a one-way door: an advisor who cleared an item
            they had not actually dealt with can put it back. The endpoint always
            accepted read:false; the FE simply never sent it. */}
        <button
          type="button"
          className="k-btn k-btn--ghost k-btn--sm"
          onClick={() => onRead(item.key, !item.read)}
          data-testid={item.read ? `notif-unread-${item.key}` : `notif-read-${item.key}`}
        >
          {t(item.read ? "notifications.markUnread" : "notifications.markRead")}
        </button>
      </span>
    </li>
  );
}
